<template>
  <v-layout>
    <ClassicScripts />
    <Tracking />
    <v-main>
      <SideMenu
      v-if="!isMobile"
      :leftMenuItems="leftMenuItems"
      :loading="menuLoading"
      :memberData="memberData"
      :myRankingsData="myRankingsData"
      :isLoggedIn="isLoggedIn"
      :isLoggedOut="isLoggedOut"
      />
      <TopBar :isLoggedIn="isLoggedIn" :isLoggedOut="isLoggedOut" />
      <AdminAcceptBanner />
      <GeneralBanner :banners="banners" />
      <ToastMessages />
      <ModalPopup
      :title="modalStore.title ?? ''"
      :description="modalStore.description ?? ''"
      :pageComponent="modalStore.component"
      :isVisible="modalStore.isVisible"
      :pageComponentProps="modalStore.componentProps"
      :color="modalStore.componentProps.color"
      :noPadding="modalStore.noPadding"
      :closeIcon="modalStore.closeIcon"
      @update:modelValue="modalStore.closeModal"
      />
      <PullDownRefresh v-if="isNativeApp">
        <RouterView />
      </PullDownRefresh>
      <RouterView v-else />
      <MobilePanel v-if="isMobile" :loading="menuLoading" />
      <MobileMenu v-if="isMobile" />
      <SiteFooter :memberData="memberData" :myRankingsData="myRankingsData" :moreData="moreData" />
      <ZendeskHelpButton v-if="!isMobile" />
    </v-main>
  </v-layout>
</template>

<script lang="ts" setup>

import { useRoute } from 'vue-router';
import { useTheme, useDisplay } from 'vuetify';
import { useModalStore } from '@/store/modalStore';
import { useUserStore } from '@/store/userJWTStore';
import { useUserDataStore } from '@/store/userDataStore';
import { SafeArea } from '@capacitor-community/safe-area';
import { isMobileApp } from '@/services/mobile_app/mobileAppServices';
import { ListenForPageRefresh } from '@/services/tools/RefreshService';
import { sportTitle, getResourceUrl } from '@/services/admin_actions/apiUtils';
import { onMounted, onBeforeUnmount, onBeforeMount, computed, watch, ref } from 'vue';
import { registerPushNotifications } from '@/services/notifications/PushNotificationService';

import TopBar from './navigation/TopBar.vue';
import SideMenu from './navigation/SideMenu.vue';
import SiteFooter from './navigation/SiteFooter.vue';
import MobileMenu from './navigation/MobileMenu.vue';
import MobilePanel from './navigation/MobilePanel.vue';
import Tracking from '@/components/GlobalComponents/Tracking.vue';
import ModalPopup from '@/components/ComponentLibrary/ModalPopup.vue';
import ToastMessages from '@/components/FeedBackComponents/ToastMessages.vue';
import ClassicScripts from '@/components/GlobalComponents/ClassicScripts.vue';
import GeneralBanner from '@/components/NotificationBanners/GeneralBanner.vue';
import PullDownRefresh from '@/components/GlobalComponents/PullDownRefresh.vue';
import ZendeskHelpButton from '@/components/GlobalComponents/ZendeskHelpButton.vue';
import AdminAcceptBanner from '@/components/NotificationBanners/AdminAcceptBanner.vue';

// Define the theme
const theme = useTheme();
const themeName = import.meta.env.VITE_SPORT_THEME;
theme.global.name.value = themeName;

// Function to Show/Hide components in child components
const store = useUserStore();
const isLoggedIn = computed(() => store.isLoggedIn);
const isLoggedOut = computed(() => store.isLoggedOut);

// Function to toggle the Side Menu drawer state
const drawerOpen = ref(window.innerWidth > 767);
const toggleDrawer = () => {
  drawerOpen.value = !drawerOpen.value;
};

// Function to hide/show components based on screen size
const isMobile = ref(window.innerWidth < 767);
const isDesktop = ref(window.innerWidth >= 767);

// Detect if the app is running on a native mobile platform
const nativeAppDetected = ref(false)
onMounted(() => { nativeAppDetected.value = isMobileApp() })
const isNativeApp = computed(() => nativeAppDetected.value)

// Update the mobile/desktop state on mount
onMounted(() => {
  const update = () => {
    isMobile.value = window.innerWidth < 767;
    isDesktop.value = window.innerWidth >= 767;
  };
  window.addEventListener('resize', update);
  onBeforeUnmount(() => window.removeEventListener('resize', update));
});

// Add function to control the modal store
const modalStore = useModalStore();

// Pinia Store
const userDataStore = useUserDataStore();
onBeforeMount(async () => {
  await userDataStore.fetchUserData().then(() => {
    userDataStore.checkForTriggers();
  });
});

const banners = computed(() => userDataStore.banners);
const leftMenuItems = computed(() => userDataStore.menuData.left_menu_items);
const memberData = computed(() => userDataStore.menuData.site_map['Members area']);
const myRankingsData = computed(() => userDataStore.menuData.site_map['My rankings']);
const moreData = computed(() => userDataStore.menuData.site_map['About']);

registerPushNotifications();
ListenForPageRefresh(); // Listen for page refresh events

// Watcher to monitor changes in leftMenuItems
const menuLoading = ref(true);
watch(leftMenuItems, () => {
  menuLoading.value = false;
});

// Watch for route changes
const route = useRoute();
watch(
() => route.fullPath,
() => {}
);

// Watch for changes in the content
watch(
() => userDataStore.menuData,
() => {},
{ deep: true }
);

// Initialize on mount
// Add window resize listener to adjust drawer visibility based on window size
onMounted(() => {
  // Inject WebPage Schema
  injectWebPageJsonLd();
});

// Add Schema dynamically to the page for WebPage
const injectWebPageJsonLd = () => {
  const script = document.createElement('script');
  script.type = 'application/ld+json';
  
  // Define the correct description based on the sport
  const sport = sportTitle.value.toLowerCase();
  const description =
  sport === 'padel' || sport === 'tennis'
  ? 'Find your level, track your progress. From the team behind SquashLevels.'
  : `${sportTitle.value}Levels has established itself as ${sport}’s premier tool for measuring performance of global ${sport} players and is backed by the World ${sportTitle.value} Federation.`;
  
  script.text = JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    url: window.location.origin,
    name: document.title || `${sportTitle.value}Levels – Official ${sportTitle.value} Rankings`,
    description: description,
    image: getResourceUrl('/cdn/' + sportTitle.value.toLowerCase() + '/site/search-background.jpg'),
    publisher: {
      '@type': 'Organization',
      name: `${sportTitle.value}Levels`,
      logo: getResourceUrl('/cdn/' + sportTitle.value.toLowerCase() + '/site/search-background.jpg'),
    },
  });
  
  document.head.appendChild(script);
};
</script>

<style lang="scss">

// Less Than ≤ 1024px: Show mobile menu, hide community menu
@media (max-width: $tablet-screen) {
  .v-main {
    padding-top: calc(64px + var(--safe-area-inset-top, 0px)) !important;
    padding-bottom: calc(64px + var(--safe-area-inset-bottom, 0px)) !important;
  }
}

// Temp fix for content hidden behind the side menu
@media (min-width: 768px) and (max-width: 1279px) {
  .v-main {
    padding-left: 54px !important;
  }
  .navigation_menu_bar .v-toolbar__content {
    padding-left: 54px !important;
  }
}

// Less Than ≤ 767px: Show mobile menu, hide community menu
@media (max-width: $mobile-screen) {
  .v-main {
    padding-top: calc(50px + var(--safe-area-inset-top, 0px)) !important;
    padding-bottom: calc(50px + var(--safe-area-inset-bottom, 0px)) !important;
  }
}
</style>
